import { StepLayoutGroupSchema, type StepLayoutGroup } from "../types";

/**
 * Step layout groups are decoration, so every operation on them is forgiving: a group
 * that references a deleted step, or a payload with a garbled entry, must never
 * be able to fail a save or corrupt a graph. These helpers are shared by the
 * editor and the persistence layer so both sides normalise identically.
 */

/**
 * Drop step references that no longer exist in the graph.
 *
 * Steps get deleted without the client necessarily rewriting every group that
 * mentioned them (and a concurrent draft edit can delete a step between two
 * saves), so membership is pruned against the authoritative step list on the
 * way in. Empty groups survive — a labelled empty box is a legitimate thing to
 * leave on the canvas while building.
 */
export function pruneStepLayoutGroupMembers(
  groups: StepLayoutGroup[],
  validStepIds: Iterable<string>,
): StepLayoutGroup[] {
  const valid = new Set(validStepIds);
  const groupIds = new Set(groups.map((group) => group.id));

  /**
   * A `parentGroupId` is only meaningful if it names a group in this same set and
   * doesn't close a loop. Neither is reachable through the editor, but this
   * column is free-form JSONB on the wire, and a cycle here would hang every
   * consumer that walks the chain to compute absolute positions — so it's cut
   * here rather than defended against everywhere downstream.
   */
  const parentById = new Map(
    groups.map((group) => [group.id, group.parentGroupId ?? null]),
  );
  function hasCycle(startId: string): boolean {
    const seen = new Set<string>([startId]);
    let current = parentById.get(startId) ?? null;
    while (current !== null) {
      if (seen.has(current)) return true;
      seen.add(current);
      current = parentById.get(current) ?? null;
    }
    return false;
  }

  return groups.map((group) => {
    const stepIds = group.stepIds.filter((stepId) => valid.has(stepId));
    const parentGroupId = group.parentGroupId ?? null;
    const parentIsUsable =
      parentGroupId !== null &&
      parentGroupId !== group.id &&
      groupIds.has(parentGroupId) &&
      !hasCycle(group.id);

    const nextParent = parentIsUsable ? parentGroupId : null;
    if (
      stepIds.length === group.stepIds.length &&
      nextParent === parentGroupId
    ) {
      return group;
    }
    return { ...group, stepIds, parentGroupId: nextParent };
  });
}

/**
 * Rewrite group membership through a step-id remap. Used when a version is
 * cloned into a new draft: every step gets a fresh UUID, so groups pointing at
 * the source step ids would otherwise all come out empty. Unmapped ids are
 * dropped rather than carried over, since a stale id from another version would
 * silently pull an unrelated step into the group.
 */
export function remapStepLayoutGroupStepIds(
  groups: StepLayoutGroup[],
  stepIdMap: Map<string, string>,
): StepLayoutGroup[] {
  return groups.map((group) => ({
    ...group,
    stepIds: group.stepIds
      .map((stepId) => stepIdMap.get(stepId))
      .filter((stepId): stepId is string => stepId !== undefined),
  }));
}

/**
 * Coerce an untrusted value (a JSONB column read, an imported workflow file)
 * into a valid group list, discarding entries that don't parse instead of
 * throwing. A malformed decoration must not make a workflow unopenable.
 */
export function parseStepLayoutGroups(value: unknown): StepLayoutGroup[] {
  if (!Array.isArray(value)) return [];
  const groups: StepLayoutGroup[] = [];
  for (const entry of value) {
    const parsed = StepLayoutGroupSchema.safeParse(entry);
    if (parsed.success) groups.push(parsed.data);
  }
  return groups;
}
